/**
 * Formation picker — single chip showing the current formation code,
 * click to open a dropdown grouped into Standard / Extended / Custom.
 *
 * Replaces both the old FormationChips horizontal strip AND the
 * separate FormationLibraryDrawer that used to live behind the
 * Library menu. All 11v11 formations + coach-saved customs are now
 * reachable from one chip per team row. A "Save current as custom…"
 * footer captures the current pitch state as a new TacticsCustomFormation.
 *
 * Popover is portal-rendered into document.body so the HUD row's
 * backdrop-filter stacking context doesn't clip it.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HudChip } from './HudChip';
import { SUPPORTED_FORMATIONS } from '../tactics3d/formationProjection';
import { EXTENDED_FORMATIONS } from '@4cm/core/tactics/formationsExtended';
import type { TacticsCustomFormation } from '@4cm/core/tactics/formationsExtended';
import './FormationDropdown.css';

export interface FormationDropdownProps {
  /** Current formation code (either a SUPPORTED, EXTENDED, or custom id). */
  current: string;
  /** Coach picks a formation. Receives a code (`4-3-3`) or a custom id. */
  onChange: (codeOrId: string) => void;
  /** Coach-saved formations to surface in the Custom section. */
  customs?: ReadonlyArray<TacticsCustomFormation>;
  /** Save the CURRENT board layout as a new custom — prompts the
   *  coach for a name + calls back. Omit to hide the footer. */
  onSaveCustom?: (name: string) => void;
  team?: 'home' | 'away';
}

export function FormationDropdown({
  current,
  onChange,
  customs = [],
  onSaveCustom,
  team,
}: FormationDropdownProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; bottom: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      setPos({
        left: rect.left,
        bottom: window.innerHeight - rect.top + 6,
      });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e: PointerEvent) => {
      const trigger = triggerRef.current;
      const pop = popoverRef.current;
      if (e.target instanceof Node) {
        if (trigger?.contains(e.target)) return;
        if (pop?.contains(e.target)) return;
      }
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => {
      window.addEventListener('pointerdown', onPointer);
    }, 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
      clearTimeout(t);
    };
  }, [open]);

  const extendedCodes = Object.keys(EXTENDED_FORMATIONS).filter(
    (c) => !SUPPORTED_FORMATIONS.includes(c),
  );

  // The "label" shown on the trigger chip: if the current value is a
  // custom id, surface the custom's name; otherwise the code itself.
  const triggerLabel =
    customs.find((c) => c.id === current)?.name ?? current;

  const handleSaveClick = () => {
    if (!onSaveCustom) return;
    const name = window.prompt('Save current shape as custom formation:', `My ${current}`);
    if (!name || !name.trim()) return;
    setOpen(false);
    onSaveCustom(name.trim());
  };

  return (
    <div
      className="formation-dropdown"
      ref={triggerRef}
      data-testid={`formation-dropdown-${team ?? 'home'}`}
    >
      <HudChip
        active={open}
        team={team}
        ariaLabel={`Change ${team ?? 'home'} formation (current: ${triggerLabel})`}
        onClick={() => setOpen((o) => !o)}
      >
        {triggerLabel} ▾
      </HudChip>
      {open && pos && typeof document !== 'undefined' && createPortal(
        <div
          ref={popoverRef}
          className="formation-dropdown__popover"
          role="menu"
          aria-label={`${team ?? 'home'} formation options`}
          data-testid={`formation-dropdown-popover-${team ?? 'home'}`}
          style={{ left: pos.left, bottom: pos.bottom }}
        >
          <Section title="Standard">
            {SUPPORTED_FORMATIONS.map((code) => (
              <Item
                key={code}
                label={code}
                active={code === current}
                onClick={() => {
                  setOpen(false);
                  if (code !== current) onChange(code);
                }}
              />
            ))}
          </Section>
          {extendedCodes.length > 0 && (
            <Section title="Extended">
              {extendedCodes.map((code) => (
                <Item
                  key={code}
                  label={code}
                  active={code === current}
                  onClick={() => {
                    setOpen(false);
                    if (code !== current) onChange(code);
                  }}
                />
              ))}
            </Section>
          )}
          {customs.length > 0 && (
            <Section title="Custom">
              {customs.map((cf) => (
                <Item
                  key={cf.id}
                  label={cf.name}
                  active={cf.id === current}
                  onClick={() => {
                    setOpen(false);
                    if (cf.id !== current) onChange(cf.id);
                  }}
                />
              ))}
            </Section>
          )}
          {onSaveCustom && (
            <div className="formation-dropdown__footer">
              <button
                type="button"
                className="formation-dropdown__save"
                onClick={handleSaveClick}
              >
                + Save current shape…
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="formation-dropdown__section" role="group" aria-label={title}>
      <div className="formation-dropdown__section-label">{title}</div>
      <div className="formation-dropdown__section-grid">{children}</div>
    </div>
  );
}

function Item({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      className="formation-dropdown__item"
      data-active={active || undefined}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
